import {Trans, useLingui} from "@lingui/react/macro";
import copy from "copy-to-clipboard";
import {QRCodeSVG} from "qrcode.react";
import {useState, type ReactNode} from "react";
import {useNavigate} from "react-router-dom";
import * as v from "valibot";
import {getShareGroupLink} from "../../features/auth/client-auth";
import {TextField} from "../../features/form/fields";
import {useForm} from "../../features/form/Form";
import {MiniPill} from "../../features/items/ItemPill";
import {addMessage} from "../../features/messenger/messenger";
import createId, {IdType} from "../../models/create-id";
import {deleteGroup} from "../../models/group-model-helpers";
import type {GroupEntry, GroupId, GroupRepo, UnitEntry} from "../../models/GroupRepo";
import {useSortedUnits} from "../../models/unit-queries";
import type {UserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import GroupFooterNav from "../../shared/ui/GroupFooterNav";
import Header from "../../shared/ui/Header";
import {Col, css, Row, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import {ControlledDrawer} from "../../ui/Drawer";
import Icon from "../../ui/Icon";
import {Input} from "../../ui/Input";
import {createSorter} from "../../utils/sort-utils";

const Block = ({children, title}: {children: ReactNode; title: ReactNode}) => (
  <Col rounded="4" surface pa="16" sp="12">
    <Text as="h2" size="20">
      {title}
    </Text>
    {children}
  </Col>
);

type GroupProps = {group: GroupEntry; groupRepo: GroupRepo; userRepo: UserRepo};

const renameFormSchema = v.object({
  groupName: v.pipe(v.string(), v.nonEmpty(), v.maxLength(256)),
});

const Rename = ({group, groupRepo}: GroupProps) => {
  const {Form, field} = useForm({
    initial: {groupName: group.name},
    schema: renameFormSchema,
    onSubmit: ({groupName}) => {
      groupRepo.vals.patch({name: groupName});
    },
  });
  return (
    <Form
      buttonLabel={<Trans>Rename</Trans>}
      className={css({display: "flex", sp: "8", align: "end"})}
    >
      <TextField label={<Trans>Name</Trans>} field={field("groupName")} />
    </Form>
  );
};

const UnitPill = ({unit, groupRepo}: {unit: UnitEntry; groupRepo: GroupRepo}) => {
  const handleDelete = () => {
    groupRepo.Units.delete(unit.id);
  };
  return (
    <Row sp="4" align="center">
      <MiniPill>{unit.name}</MiniPill>
      <Button variant="tertiary" onClick={handleDelete} size="sm">
        <Icon name="trash" size="16" />
      </Button>
    </Row>
  );
};

const addUnitFormSchema = v.object({
  unitName: v.pipe(v.string(), v.nonEmpty(), v.maxLength(8)),
});

const Units = ({groupRepo}: GroupProps) => {
  const units = useSortedUnits(groupRepo);

  const {Form, field, reset} = useForm({
    initial: {unitName: ""},
    schema: addUnitFormSchema,
    onSubmit: ({unitName}) => {
      const lastSortIndex = units.length > 0 ? units[units.length - 1].sortIndex : "";
      const sortIndex = createSorter().addAtEnd({
        currLastValue: lastSortIndex,
        expectedRemainingCount: 10,
      });
      const unitId = createId(IdType.Unit);
      groupRepo.Units.set(unitId, {
        id: unitId,
        name: unitName,
        sortIndex: sortIndex,
        createdAt: new Date(),
      });
      reset();
    },
  });

  return (
    <Col sp="8">
      <Text as="h3" color="secondary" size="14_tight" weight="bold">
        <Trans>Units</Trans>
      </Text>
      <Row wrap sp="12" align="center">
        <MiniPill>
          <Trans id="units.piece">pcs</Trans>
        </MiniPill>
        {units.map((unit) => (
          <UnitPill key={unit.id} unit={unit} groupRepo={groupRepo} />
        ))}
      </Row>
      <Form
        buttonLabel={<Trans>Add</Trans>}
        className={css({display: "flex", sp: "8", align: "end"})}
      >
        <TextField label={<Trans>New Unit</Trans>} field={field("unitName")} />
      </Form>
    </Col>
  );
};

const Configure = (props: GroupProps) => (
  <Block title={<Trans>Basics</Trans>}>
    <Rename {...props} />
    <Units {...props} />
  </Block>
);

// const MemberList = ({groupRepo, userRepo}: {groupRepo: GroupRepo; userRepo: UserRepo}) => {
//   const me = userRepo.vals.useValues();
//   const members = Object.values(groupRepo.Members.useGetAll());
//   if (!members.length) return <div>no members</div>;
//   return (
//     <div>
//       members: {members.map((m) => (m.id === me.id ? "You" : m.name)).join(", ")}
//     </div>
//   );
// };

type DeleteModalContentProps = GroupProps & {
  onClose: () => void;
};

const DeleteModalContent = ({group, groupRepo, userRepo, onClose}: DeleteModalContentProps) => {
  const navigate = useNavigate();
  const handleDelete = () => {
    deleteGroup(groupRepo, userRepo, group.id);
    navigate("/");
  };
  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <Text size="14_tight">
        <Trans>Are you sure!?</Trans>
      </Text>
      <Row sp="4">
        <Button onClick={onClose}>
          <Trans>no</Trans>
        </Button>{" "}
        <Button onClick={handleDelete}>
          <Trans>yes</Trans>
        </Button>
      </Row>
    </Col>
  );
};

const DeleteButton = (props: GroupProps) => {
  const {t} = useLingui();
  const [showWarning, setShowWarning] = useState(false);
  const off = () => setShowWarning(false);
  const on = () => setShowWarning(true);
  return (
    <>
      <ControlledDrawer open={showWarning} onClose={off} title={t`Delete List`}>
        <DeleteModalContent {...props} onClose={off} />
      </ControlledDrawer>
      <Button onClick={on}>
        <Trans>Delete List</Trans>
      </Button>
    </>
  );
};

const Danger = (props: GroupProps) => (
  <Block title={<Trans>Danger Zone</Trans>}>
    <DeleteButton {...props} />
  </Block>
);

const Share = ({groupId, userRepo}: {groupId: GroupId; userRepo: UserRepo}) => {
  const [link, setLink] = useState<null | string>(null);
  const handleStart = async () => {
    setLink(await getShareGroupLink({groupId, userRepo}));
  };

  const handleCopyClick = (url: string) => {
    copy(url);
    addMessage(<Trans>Url copied</Trans>);
  };

  return (
    <Block title={<Trans>Share Group</Trans>}>
      {link ? (
        <Col sp="24">
          <Text>
            <Trans>Scan QR code or share link</Trans>
          </Text>
          <QRCodeSVG value={link} className={css({pa: "4"})} style={{backgroundColor: "#fff"}} />
          <Row sp="8">
            <Input value={link} readOnly />
            <Button onClick={() => handleCopyClick(link)}>
              <Trans>Copy</Trans>
            </Button>
          </Row>
          <Text>
            <Trans>Code is valid for 5 minutes.</Trans>
          </Text>
        </Col>
      ) : (
        <Col align="start">
          <Button onClick={handleStart}>
            <Trans>Create Link</Trans>
          </Button>
        </Col>
      )}
    </Block>
  );
};

const GroupSettings = ({groupRepo, userRepo}: {groupRepo: GroupRepo; userRepo: UserRepo}) => {
  const group = groupRepo.vals.useValues();
  const props = {group, groupRepo, userRepo};
  return (
    <AppShell
      header={
        <Header
          title={<Trans>Settings</Trans>}
          backButton={{to: "..", label: <Trans id="nav.back">Back</Trans>}}
        />
      }
      footer={<GroupFooterNav current="list" group={group} />}
    >
      <Col px="12" sp="24" py="24">
        <Col align="start">
          <Button to="../categories">
            <Trans>Change Order</Trans>
          </Button>
        </Col>
        <Configure {...props} />
        <Share groupId={group.id} userRepo={userRepo} />
        <Danger {...props} />
      </Col>
    </AppShell>
  );
};

export default GroupSettings;
